import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Building2,
  Mail,
  Phone,
  Calendar,
  Clock,
  FileText,
  Check,
  X as XIcon,
  Printer,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import toast from "react-hot-toast";
import { Button, Card, StatusBadge, Modal } from "@/components/ui";
import { publicApi } from "@/services/api";

export default function PublicBudgetPage() {
  const { token } = useParams();

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [approveNotes, setApproveNotes] = useState("");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["public-budget", token],
    queryFn: () => publicApi.getBudget(token!),
    enabled: !!token,
  });

  const approveMutation = useMutation({
    mutationFn: () => publicApi.approveBudget(token!, { notes: approveNotes }),
    onSuccess: () => {
      toast.success("Orçamento aprovado com sucesso!");
      refetch();
      setShowApproveModal(false);
    },
    onError: () => {
      toast.error("Erro ao aprovar orçamento");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => publicApi.rejectBudget(token!, rejectReason),
    onSuccess: () => {
      toast.success("Orçamento recusado");
      refetch();
      setShowRejectModal(false);
    },
    onError: () => {
      toast.error("Erro ao recusar orçamento");
    },
  });

  const budget = data?.data;
  const company = budget?.company;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (error || !budget) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <XIcon className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            Orçamento não encontrado
          </h1>
          <p className="text-text-secondary">
            Este link pode estar expirado ou o orçamento foi removido.
          </p>
        </Card>
      </div>
    );
  }

  const canRespond = ["sent", "viewed"].includes(budget.status);
  const isExpired =
    budget.valid_until && new Date(budget.valid_until) < new Date();

  return (
    <div className="min-h-screen bg-dark">
      {/* Header */}
      <header className="bg-bg-secondary border-b border-white/10 py-6 print:py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              {company?.logo_url ? (
                <img
                  src={company.logo_url}
                  alt={company.name}
                  className="h-12 object-contain"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-text-primary">
                  {company?.name}
                </h1>
                {company?.website && (
                  <p className="text-sm text-text-muted">{company.website}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              <Button variant="secondary" size="sm" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Imprimir
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Budget Header */}
          <Card className="p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-3xl font-bold font-mono text-primary">
                    {budget.number}
                  </h2>
                  <StatusBadge status={budget.status} />
                </div>
                {budget.title && (
                  <h3 className="text-xl text-text-primary mb-2">
                    {budget.title}
                  </h3>
                )}
                <p className="text-text-muted">
                  Emitido em{" "}
                  {format(
                    new Date(budget.created_at),
                    "dd 'de' MMMM 'de' yyyy",
                    { locale: ptBR },
                  )}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-text-muted">Valor Total</p>
                <p className="text-3xl font-bold font-mono text-primary">
                  {formatCurrency(budget.total_amount)}
                </p>
                {budget.valid_until && (
                  <p
                    className={`text-sm mt-2 ${isExpired ? "text-red-400" : "text-text-muted"}`}
                  >
                    <Calendar className="w-4 h-4 inline-block mr-1" />
                    Válido até{" "}
                    {format(new Date(budget.valid_until), "dd/MM/yyyy")}
                    {isExpired && " (Expirado)"}
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* Client Info */}
          {budget.client && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Para
              </h3>
              <div className="space-y-2">
                <p className="text-xl font-medium text-text-primary">
                  {budget.client.name}
                </p>
                {budget.client.email && (
                  <p className="text-text-muted flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {budget.client.email}
                  </p>
                )}
                {budget.client.phone && (
                  <p className="text-text-muted flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {budget.client.phone}
                  </p>
                )}
              </div>
            </Card>
          )}

          {/* Description */}
          {budget.description && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Descrição
              </h3>
              <p className="text-text-secondary whitespace-pre-wrap">
                {budget.description}
              </p>
            </Card>
          )}

          {/* Items */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              Itens
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-text-secondary font-medium">
                      Descrição
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary font-medium">
                      Qtd
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary font-medium">
                      Valor Unit.
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary font-medium">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {budget.items?.map((item: any, index: number) => {
                    const itemTotal = item.quantity * item.unit_price;
                    const discount =
                      item.discount_type === "percent"
                        ? itemTotal * (item.discount / 100)
                        : item.discount || 0;
                    const total = itemTotal - discount;

                    return (
                      <tr
                        key={item.id || index}
                        className="border-b border-white/5"
                      >
                        <td className="py-4 px-4">
                          <p className="text-text-primary">
                            {item.description}
                          </p>
                        </td>
                        <td className="py-4 px-4 text-right font-mono text-text-secondary">
                          {item.quantity} {item.unit}
                        </td>
                        <td className="py-4 px-4 text-right font-mono text-text-secondary">
                          {formatCurrency(item.unit_price)}
                        </td>
                        <td className="py-4 px-4 text-right font-mono font-medium text-text-primary">
                          {formatCurrency(total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-6 pt-6 border-t border-white/10 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-text-secondary">Subtotal</span>
                <span className="font-mono text-text-primary">
                  {formatCurrency(budget.subtotal)}
                </span>
              </div>
              {budget.discount > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Desconto</span>
                  <span className="font-mono text-red-400">
                    -{formatCurrency(budget.discount_amount || 0)}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-white/10">
                <span className="text-lg font-semibold text-text-primary">
                  Total
                </span>
                <span className="font-mono text-2xl font-bold text-primary">
                  {formatCurrency(budget.total_amount)}
                </span>
              </div>
            </div>
          </Card>

          {/* Terms */}
          {(budget.payment_terms ||
            budget.delivery_time ||
            budget.warranty) && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Condições
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {budget.payment_terms && (
                  <div>
                    <h4 className="text-sm font-medium text-text-secondary mb-2">
                      Pagamento
                    </h4>
                    <p className="text-text-primary whitespace-pre-wrap">
                      {budget.payment_terms}
                    </p>
                  </div>
                )}
                {budget.delivery_time && (
                  <div>
                    <h4 className="text-sm font-medium text-text-secondary mb-2">
                      Prazo de Entrega
                    </h4>
                    <p className="text-text-primary whitespace-pre-wrap">
                      {budget.delivery_time}
                    </p>
                  </div>
                )}
                {budget.warranty && (
                  <div>
                    <h4 className="text-sm font-medium text-text-secondary mb-2">
                      Garantia
                    </h4>
                    <p className="text-text-primary whitespace-pre-wrap">
                      {budget.warranty}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Notes */}
          {budget.notes && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Observações
              </h3>
              <p className="text-text-secondary whitespace-pre-wrap">
                {budget.notes}
              </p>
            </Card>
          )}

          {/* Company Footer */}
          <Card className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-text-muted">
              <div>
                <p className="font-medium text-text-primary">{company?.name}</p>
                {company?.document && <p>CNPJ: {company.document}</p>}
              </div>
              <div className="flex flex-col md:items-end gap-1">
                {company?.email && (
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {company.email}
                  </p>
                )}
                {company?.phone && (
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {company.phone}
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          {canRespond && !isExpired && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center print:hidden">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => setShowRejectModal(true)}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <XIcon className="w-5 h-5 mr-2" />
                Recusar Orçamento
              </Button>
              <Button
                size="lg"
                onClick={() => setShowApproveModal(true)}
                className="bg-green-600 hover:bg-green-500"
              >
                <Check className="w-5 h-5 mr-2" />
                Aprovar Orçamento
              </Button>
            </div>
          )}

          {/* Already Responded */}
          {budget.status === "approved" && (
            <Card className="p-6 border-green-500/30 bg-green-500/5 text-center">
              <Check className="w-12 h-12 text-green-400 mx-auto mb-2" />
              <h3 className="text-xl font-semibold text-green-400">
                Orçamento Aprovado
              </h3>
              <p className="text-text-muted mt-2">
                Este orçamento foi aprovado em{" "}
                {budget.approved_at &&
                  format(new Date(budget.approved_at), "dd/MM/yyyy HH:mm")}
              </p>
            </Card>
          )}

          {budget.status === "rejected" && (
            <Card className="p-6 border-red-500/30 bg-red-500/5 text-center">
              <XIcon className="w-12 h-12 text-red-400 mx-auto mb-2" />
              <h3 className="text-xl font-semibold text-red-400">
                Orçamento Recusado
              </h3>
              {budget.rejection_reason && (
                <p className="text-text-muted mt-2">
                  Motivo: {budget.rejection_reason}
                </p>
              )}
            </Card>
          )}

          {isExpired &&
            budget.status !== "approved" &&
            budget.status !== "rejected" && (
              <Card className="p-6 border-yellow-500/30 bg-yellow-500/5 text-center">
                <Clock className="w-12 h-12 text-yellow-400 mx-auto mb-2" />
                <h3 className="text-xl font-semibold text-yellow-400">
                  Orçamento Expirado
                </h3>
                <p className="text-text-muted mt-2">
                  Este orçamento estava válido até{" "}
                  {format(new Date(budget.valid_until), "dd/MM/yyyy")}
                </p>
              </Card>
            )}
        </div>
      </main>

      {/* Approve Modal */}
      <Modal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        title="Aprovar Orçamento"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            Confirma a aprovação deste orçamento no valor de{" "}
            <span className="font-mono font-bold text-primary">
              {formatCurrency(budget.total_amount)}
            </span>
            ?
          </p>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Observações (opcional)
            </label>
            <textarea
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              placeholder="Alguma observação adicional..."
              className="w-full h-24 px-4 py-3 bg-dark-lighter border border-white/10 rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-primary resize-none"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowApproveModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => approveMutation.mutate()}
              isLoading={approveMutation.isPending}
              className="bg-green-600 hover:bg-green-500"
            >
              <Check className="w-4 h-4 mr-2" />
              Confirmar Aprovação
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Recusar Orçamento"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            Tem certeza que deseja recusar este orçamento?
          </p>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Motivo da recusa (opcional)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Informe o motivo da recusa..."
              className="w-full h-24 px-4 py-3 bg-dark-lighter border border-white/10 rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-primary resize-none"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowRejectModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => rejectMutation.mutate()}
              isLoading={rejectMutation.isPending}
            >
              <XIcon className="w-4 h-4 mr-2" />
              Confirmar Recusa
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
